import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Store } from 'lucide-react';
import { AppLayout } from '@/components/layout/app-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { prixiaService } from '../services/prixia-service';
import type { EjeDeTiendas, FilaDeTiendaPrixia, TipoDeLectura } from '../types/prixia.types';
import { SALUD, TIPO_DE_LECTURA, fechaYHora, frecuenciaEnTexto } from '../lib/prixia-textos';
import { AltaTiendaPrixiaDialog } from '../components/alta-tienda-prixia-dialog';
import { RubrosDeTiendasPrixia } from '../components/rubros-de-tiendas-prixia';
import { avisoDelCatalogo, estadoDelCatalogo } from '@/lib/frescura-del-catalogo';

/**
 * Las tiendas del catálogo de Prixia (spec 288 C3, C7; plan 297). Cada una se lee una
 * sola vez y lo leído lo ven todas las empresas que la tienen habilitada.
 */
export function AdminTiendasPrixiaPage() {
  const navigate = useNavigate();
  const [tiendas, setTiendas] = useState<FilaDeTiendaPrixia[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [alta, setAlta] = useState(false);
  // Reglas de la demo (343): los rubros. Si no se leen, el catálogo se ve igual (sin esa tarjeta).
  const [ejes, setEjes] = useState<EjeDeTiendas[] | null>(null);

  const cargarEjes = useCallback(async () => {
    try {
      setEjes(await prixiaService.ejes());
    } catch {
      setEjes(null);
    }
  }, []);

  // Un error de carga NO se muestra como catálogo vacío: «Todavía no hay tiendas» sería
  // falso y llevaría a dar de alta tiendas que ya están.
  const cargar = useCallback(async () => {
    setError(null);
    setCargando(true);
    try {
      setTiendas(await prixiaService.listarTiendas());
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo leer el catálogo de tiendas.'));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
    void cargarEjes();
  }, [cargar, cargarEjes]);

  const nombreDelRubro = (t: FilaDeTiendaPrixia) =>
    t.ejeId ? (ejes?.find((e) => e.id === t.ejeId)?.nombre ?? null) : null;

  const calendarioDe = (t: FilaDeTiendaPrixia, tipo: TipoDeLectura) =>
    t.calendario.find((c) => c.type === tipo);

  return (
    <AppLayout variante="plataforma">
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <Store className="h-6 w-6" aria-hidden="true" />
              Tiendas de Prixia
            </h1>
            <p className="text-sm text-muted-foreground">
              Las tiendas que lee Prixia. Cada una se lee una sola vez y lo leído lo ven todas las
              empresas que la tienen habilitada. Nada de lo leído se borra.
            </p>
          </div>
          <Button onClick={() => setAlta(true)}>
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            Agregar tienda
          </Button>
        </div>

        {error && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void cargar()} disabled={cargando}>
              Reintentar
            </Button>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {tiendas && !error
                ? `${tiendas.length} ${tiendas.length === 1 ? 'tienda' : 'tiendas'}`
                : 'Tiendas'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {tiendas === null && cargando ? (
              <div className="space-y-2 p-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : tiendas === null ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                No se pudo leer el catálogo: reintentá arriba.
              </p>
            ) : tiendas.length === 0 ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                Todavía no hay tiendas en el catálogo de Prixia.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tienda</TableHead>
                    <TableHead>Lecturas</TableHead>
                    <TableHead>Chequeo de precios</TableHead>
                    <TableHead>Barrido del catálogo</TableHead>
                    <TableHead className="text-right">Artículos publicados</TableHead>
                    <TableHead className="text-right">Empresas</TableHead>
                    <TableHead>Ahora</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiendas.map((t) => {
                    const chequeo = calendarioDe(t, 'PRICE_CHECK');
                    const barrido = calendarioDe(t, 'FULL_RESCRAPE');
                    const fallas = t.salud.fallasSeguidas;
                    return (
                      <TableRow
                        key={t.id}
                        className="cursor-pointer hover:bg-accent/30"
                        onClick={() => navigate(`/plataforma/tiendas/${t.id}`)}
                      >
                        <TableCell>
                          <div className="font-medium">{t.name}</div>
                          <div className="text-xs text-muted-foreground">{t.url}</div>
                          {ejes && (
                            <div className="text-xs text-muted-foreground">
                              {`Rubro: ${nombreDelRubro(t) ?? 'sin rubro'}`}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={SALUD[t.salud.estado].variante} title={t.salud.frase}>
                            {SALUD[t.salud.estado].texto}
                          </Badge>
                          {fallas > 0 && (
                            <span className="ml-2 text-xs text-destructive">
                              {`${fallas} ${fallas === 1 ? 'falla' : 'fallas seguidas'}`}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {frecuenciaEnTexto(chequeo)}
                          <span className="block text-xs text-muted-foreground">
                            {`Próximo: ${chequeo?.isActive ? fechaYHora(chequeo.nextRunAt) : '—'}`}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">
                          {frecuenciaEnTexto(barrido)}
                          <span className="block text-xs text-muted-foreground">
                            {`Último: ${fechaYHora(t.lastSweepAt)}`}
                          </span>
                          {(() => {
                            // L3 (vault 276, PD-08): el catálogo viejo se ve desde acá,
                            // donde se enciende el barrido.
                            const aviso = avisoDelCatalogo(
                              estadoDelCatalogo({ ultimoBarridoEn: t.lastSweepAt, barrido }),
                              barrido?.isActive === true
                            );
                            return aviso ? (
                              <span className="block text-xs text-destructive">{aviso}</span>
                            ) : null;
                          })()}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {t.publicados.toLocaleString('es-BO')}
                          <span className="block text-xs text-muted-foreground">
                            {`de ${t.articulos.toLocaleString('es-BO')} vistos`}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {t.empresas}
                          {t.copiasRepetidas > 0 && (
                            <Badge variant="warning" className="ml-2">
                              {`${t.copiasRepetidas} con copia repetida`}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {t.enCurso
                            ? `${t.enCurso.status === 'RUNNING' ? 'Leyendo' : 'En cola'}: ${TIPO_DE_LECTURA[t.enCurso.type].toLowerCase()}`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {ejes && tiendas && (
          <RubrosDeTiendasPrixia
            ejes={ejes}
            tiendas={tiendas}
            onCambio={() => {
              void cargarEjes();
              void cargar();
            }}
          />
        )}

        <AltaTiendaPrixiaDialog
          abierto={alta}
          onClose={() => setAlta(false)}
          onCreada={(storeId) => {
            setAlta(false);
            navigate(`/plataforma/tiendas/${storeId}`);
          }}
        />
      </div>
    </AppLayout>
  );
}

export default AdminTiendasPrixiaPage;
